import { useQuery } from "@tanstack/react-query";
import { PROOFING_FLOW_APP_ID } from "@/config";
import { projectsGetProjectById, usersGetUserProjects } from "@/usersAndProjects-client/sdk.gen";
import type { ProjectWithPackages } from "../utils/packageEnrichment";

/**
 * Fetch user's proofing projects with their assigned packages
 *
 * This hook fetches only the projects assigned to the user (via /api/users/{sub}/projects)
 * filtered by the proofing app, then for each project fetches its full details
 * which includes assignedPackages and assignedUsers. This reduces API calls by only
 * fetching projects the user has access to, instead of all tenant projects.
 */
export interface UseFetchProjectsWithPackagesResult {
  projectsWithPackages: ProjectWithPackages[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectsWithPackages = (
  userSub: string | null | undefined,
  tenantSlug?: string,
  options?: { enabled?: boolean },
): UseFetchProjectsWithPackagesResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["projectsWithPackages", "user", userSub],
    queryFn: async () => {
      if (!userSub) {
        throw new Error("User sub is required");
      }

      // Step 1: Fetch user's projects filtered by proofing app (includes group projects)
      const projectsResponse = await usersGetUserProjects({
        path: { sub: userSub },
        query: {
          appId: PROOFING_FLOW_APP_ID,
          includeGroupProjects: "true",
        },
      });
      const projects = projectsResponse.data || [];
      console.log(
        "[projectsWithPackages] Fetched",
        projects.length,
        "proofing projects for user:",
        userSub,
      );

      // Step 2: Fetch full details for each project (includes assignedPackages and assignedUsers)
      const projectsWithPackages = await Promise.all(
        projects.map(async (project) => {
          try {
            const detailsResponse = await projectsGetProjectById({
              path: { id: project.id },
            });
            const details = detailsResponse.data;

            // Map assignedPackages to the format expected by enrichPackagesWithProjects
            const packages = details.assignedPackages.map((pkg) => ({
              id: pkg.id,
              projectId: project.id,
            }));

            return {
              ...project,
              tenantSlug: tenantSlug || "", // Use tenant slug from user context
              packages,
              assignedPackages: details.assignedPackages,
              assignedUsers: details.assignedUsers,
            };
          } catch (error) {
            // If fetching details fails for a project, return it with empty packages
            console.warn(`Failed to fetch details for project ${project.id}:`, error);
            return {
              ...project,
              tenantSlug: tenantSlug || "", // Use tenant slug from user context
              packages: [],
              assignedPackages: [],
              assignedUsers: [],
            };
          }
        }),
      );

      return projectsWithPackages;
    },
    enabled: (options?.enabled ?? true) && !!userSub,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    projectsWithPackages: data || null,
    isLoading,
    error: error as Error | null,
  };
};
